import { useState, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import MainView from "views/MainView";
import {
  IMeterHistoryBody,
  meterHistory,
  meterUser,
  _H5Event,
  handleFromAppParams,
  meterHistoryDelete,
} from "request";
import { DataList, DatePicker } from "./components";
import { IUser, IDataRow, IPagination } from "../types";
import { Spinner, Empty, LoadMore } from "components";
import { useIntlContext, SupportedLocales } from "views/IntlContext";
import { UserDropdown} from "../components"

export type IRange = { startDate?: number; endDate?: number };

type IAppParams = {
  token: string;
  lang: SupportedLocales;
  deviceId: string;
};

export const HistoryPage = () => {
  const { switchLanguage } = useIntlContext();

  const [dataLists, setDataLists] = useState<IDataRow[]>([]);

  const [userList, setUserList] = useState<IUser[]>([]);
  const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
  const [range, setRange] = useState<IRange | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState(false);

  const appParams = useRef<IAppParams | undefined>(undefined);

  const pagination = useRef<IPagination>({
    pageSize: 10,
    currentPage: 1,
  });

  const onGetHistoryList = async () => {
    const { currentPage, pageSize } = pagination.current;
    const { token, lang, deviceId } = appParams.current as IAppParams;

    if (currentPage === 1) {
      setIsLoading(true);
    }

    const params: IMeterHistoryBody = {
      token: token,
      lang: lang,
      params: {
        deviceId: deviceId as string,
        userId: currentUser?.id as string,
        size: pageSize,
        current: currentPage,
        startDate: range?.startDate,
        endDate: range?.endDate,
      },
    };

    try {
      const result = await meterHistory(params);
      setIsLoading(false);
      return result;
    } catch (error) {
      setIsLoading(false);
      console.error(
        "An error occurred while fetching the history list:",
        error
      );
      throw error;
    }
  };

  const getUserList = () => {
    meterUser({
      token: appParams.current?.token,
      lang: appParams.current?.lang,
      params: {
        deviceId: appParams.current?.deviceId as string,
      },
    })
      .then((response: any) => {
        if (response.code === "200") {
          setCurrentUser(response.result[0] as IUser);
          setUserList(response.result);
        }
      })
      .catch((err: any) => console.log(err));
  };

  // const onToggleDropdown = () => {
  //   setIsOpenDropdown((prev) => !prev);
  // };

  const onDelete = (row: IDataRow) => {
    const deleteRowIndex = dataLists.findIndex((item) => row.id === item.id);
    setDataLists((prev) => {
      const updateList = [...prev];
      updateList.splice(deleteRowIndex, 1);
      return updateList;
    });
    meterHistoryDelete({
      token: appParams.current?.token,
      lang: appParams.current?.lang,
      params: {
        id: row.id,
      },
    })
      .then(() => {})
      .catch((err: any) => console.log(err));
  };

  const onToDetailPage = (row: IDataRow) => {
    const datas = {
      event: "ToMeterDetailsPage",
      pageName: "details",
      id: row.id,
      dateStr: row.dateStr,
      deviceId: row.deviceId,
      callback: () => {},
    };
    _H5Event(datas);
  };

  const onChangeUser = (user: IUser) => {
    setIsOpenDropdown(false);
    setCurrentUser(user);
    pagination.current.currentPage = 1;
    // setIsLoading(true);
    setDataLists([]);
  };

  const onChangeRange = (newRange?: IRange) => {
    setRange(newRange);
    // setIsLoading(true);
    setIsOpenDropdown(false);
    pagination.current.currentPage = 1;
    setDataLists([]);
  };

  // 通知App当前切换的用户及当前用户是否有数据，无数据则隐藏分享按钮
  const toAppShareData = (hasData: boolean) => {
    const datas = {
      event: "SwitchUserData",
      pageName: "history",
      userId: currentUser?.id,
      hasData: hasData,
      callback: () => {},
    };
    _H5Event(datas);
  };

  const onClear = () => {
    // setIsLoading(true);
    setIsOpenDropdown(false);
    pagination.current.currentPage = 1;
    setDataLists([]);
    setRange(undefined);
  };

  const handleResponse = (response: any) => {
    const { code, result } = response;
    if (code === "200") {
      const { records, total, size, current, pages } = result;
      const list = records as IDataRow[];
      if (current === 1) {
        pagination.current = {
          pageSize: size,
          currentPage: current,
          totalPage: pages,
          totalSize: total,
        };
        if (pages > 1) {
          setHasMore(true);
        }
        setDataLists(list);
      } else {
        setDataLists((prev) => [...prev, ...list]);
      }

      if (current === pages) {
        setHasMore(false);
      }
    }
    setIsLoading(false);
  };

  const onLoadMore = async () => {
    if (isOpenDropdown) setIsOpenDropdown(false);
    const currentPage = pagination.current.currentPage + 1;
    pagination.current.currentPage = currentPage;
    const response: any = await onGetHistoryList();
    handleResponse(response);
  };

  useEffect(() => {
    const datas = {
      event: "GetHistoryParams",
      pageName: "history",
      callback: (responseStr: any) => {
        console.log("responseStr", responseStr);
        const jsonData = handleFromAppParams(responseStr);
        appParams.current = jsonData;
        if (jsonData.lang) {
          switchLanguage?.(jsonData.lang);
        }
        getUserList();
      },
    };
    _H5Event(datas);
  }, []);

  useEffect(() => {
    if(!currentUser) return 
    const fetchData = async () => {
      try {
        const response = await onGetHistoryList();
        handleResponse(response);
      } catch (error) {
        console.error("Failed to fetch the history list:", error);
      }
    };
    fetchData();
  }, [currentUser, range]);

  useEffect(() => {
    const isEmpty = dataLists.length === 0;
    if (!isLoading && currentUser) toAppShareData(!isEmpty);
  }, [dataLists]);

  return (
    <MainView backgroundClassName="bg-page dark:bg-black1b1f1e">
      <div className="flex flex-row justify-between px-4 py-3 fixed top-0 left-0 w-full bg-page dark:bg-black1b1f1e z-[1]">
        <DatePicker
          range={range}
          onChangeRange={onChangeRange}
          onClear={onClear}
        />
        <UserDropdown
          primary
          currentUser={currentUser}
          users={userList}
          onChange={onChangeUser}
        />
        {/* <User
          currentUser={currentUser}
          users={userList}
          isOpenDropdown={isOpenDropdown}
          onToggleDropdown={onToggleDropdown}
          onChange={onChangeUser}
        /> */}
      </div>
      {(isLoading || !dataLists || dataLists.length === 0) && (
        <div className="w-full h-[100vh] flex items-center justify-center">
          {isLoading ? (
            <Spinner miniSmall className={{ "border-primary": true }} />
          ) : (
            <Empty
              emptyText={<FormattedMessage id="meter.history.emptyText" />}
            />
          )}
        </div>
      )}
      {!isLoading && dataLists && dataLists.length > 0 && (
        <div className="px-4 pb-1 pt-[50px]">
          <DataList
            data={dataLists}
            onDelete={onDelete}
            onToDetailPage={onToDetailPage}
          />
          <LoadMore hasMore={hasMore} loadMore={onLoadMore} />
        </div>
      )}
    </MainView>
  );
};
